import { useCallback, useEffect, useState } from "react";

// Public daily reference rates; no account, token, or usage data is sent.
// Documentation: https://frankfurter.dev/
const RATE_URL = "https://api.frankfurter.dev/v2/rate/USD/TWD";
const CACHE_KEY = "ai_usage_usd_twd_rate_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
let memoryCache = null;
let pendingRequest = null;

function validRate(data) {
  if (!Number.isFinite(data?.rate) || data.rate <= 0) return false;
  if (typeof data.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(data.date)) {
    return false;
  }
  const date = Date.parse(`${data.date}T00:00:00Z`);
  return (
    Number.isFinite(date) &&
    new Date(date).toISOString().slice(0, 10) === data.date
  );
}

function validCache(data) {
  return (
    validRate(data) &&
    Number.isFinite(data.fetchedAt) &&
    data.fetchedAt > 0 &&
    data.fetchedAt <= Date.now()
  );
}

export function readCachedUsdTwdRate() {
  if (validCache(memoryCache)) return memoryCache;
  try {
    const cached = JSON.parse(window.localStorage.getItem(CACHE_KEY));
    if (validCache(cached)) {
      memoryCache = cached;
      return cached;
    }
  } catch {
    // Private browsing and blocked storage still allow an in-memory rate.
  }
  return null;
}

export function isUsdTwdRateFresh(data) {
  return validCache(data) && Date.now() - data.fetchedAt < CACHE_TTL_MS;
}

async function fetchRate() {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(RATE_URL, {
      signal: controller.signal,
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
    if (!response.ok) {
      throw new Error(`Exchange rate request failed (${response.status})`);
    }
    const data = await response.json();
    if (data?.base !== "USD" || data?.quote !== "TWD" || !validRate(data)) {
      throw new Error("Invalid USD/TWD exchange rate");
    }
    const cached = { rate: data.rate, date: data.date, fetchedAt: Date.now() };
    memoryCache = cached;
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
    } catch {
      // Storage is optional; the current page can still reuse this rate.
    }
    return cached;
  } finally {
    clearTimeout(timeout);
  }
}

export function getUsdTwdRate() {
  const cached = readCachedUsdTwdRate();
  if (isUsdTwdRateFresh(cached)) return Promise.resolve(cached);
  // Reopening the modal (including React Strict Mode) shares an active request.
  if (!pendingRequest) {
    pendingRequest = fetchRate().finally(() => {
      pendingRequest = null;
    });
  }
  return pendingRequest;
}

export default function useUsdTwdRate() {
  const [data, setData] = useState(readCachedUsdTwdRate);
  const [loading, setLoading] = useState(() => !isUsdTwdRateFresh(readCachedUsdTwdRate()));
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const refresh = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    const cached = readCachedUsdTwdRate();
    setData(cached);
    setError(false);
    setLoading(!isUsdTwdRateFresh(cached));
    getUsdTwdRate()
      .then((next) => {
        if (active) setData(next);
      })
      .catch(() => {
        // Keep the last known rate, with its date shown by the modal.
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  return { data, loading, error, refresh };
}
